import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { CycleQuestionnaire } from '@/types/cycle-evaluation/CycleQuestionnaire';
import type { ReviewTemplate } from '@/types/review/ReviewTemplate';
import type { OptionProps } from '@amelio/ui-library';

import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { getLocalizedText } from '@/utils/localization';

export const NO_ADDITIONAL_TEMPLATE_VALUE = '';

export type TemplatePages = { pages: { items: ReviewTemplate[]; hasNextPage: boolean }[] };

export const getPersistedTemplateId = (questionnaire?: CycleQuestionnaire): string | undefined =>
    questionnaire?.reviewTemplate?.id ?? questionnaire?.reviewTemplateId ?? undefined;

const flattenTemplatePages = (pages?: TemplatePages): ReviewTemplate[] =>
    pages?.pages.flatMap((page) => page.items) ?? [];

export const isTemplateStillAvailable = (
    templateId: string | undefined,
    templatePages?: TemplatePages,
    amelioTemplates?: ReviewTemplate[],
): boolean =>
    !!templateId &&
    [...flattenTemplatePages(templatePages), ...(amelioTemplates ?? [])].some(
        (template) => template.id === templateId,
    );

export interface TemplateAvailability {
    pages?: TemplatePages;
    amelioTemplates?: ReviewTemplate[];
    isAmelioSuccess?: boolean;
    isSuccess: boolean;
}

export type TemplateAvailabilityByType = Record<EvaluationTypeValue, TemplateAvailability>;

const isLibraryFullyLoaded = (templatePages?: TemplatePages): boolean => {
    const loadedPages = templatePages?.pages ?? [];

    return loadedPages.length > 0 && !loadedPages[loadedPages.length - 1].hasNextPage;
};

// A failed read, an unfetched page and a still-loading catalogue look exactly like a library that
// lost the template.
const isLibrarySettled = ({ pages, isAmelioSuccess, isSuccess }: TemplateAvailability): boolean =>
    isSuccess && (isAmelioSuccess ?? true) && isLibraryFullyLoaded(pages);

const flattenAllTemplates = ({
    pages,
    amelioTemplates,
}: TemplateAvailability): ReviewTemplate[] => [
    ...flattenTemplatePages(pages),
    ...(amelioTemplates ?? []),
];

export const canLibraryTestifyToDeletion = (availability: TemplateAvailability): boolean =>
    isLibrarySettled(availability) && flattenAllTemplates(availability).length > 0;

// Empty cannot tell deletion from hidden, so its stored id is answered as one never picked.
export const isLibrarySettledAndEmpty = (availability: TemplateAvailability): boolean =>
    isLibrarySettled(availability) && flattenAllTemplates(availability).length === 0;

interface ClassifyStoredTemplatesParams {
    selectedTypes: readonly EvaluationTypeValue[];
    templateIds: Partial<Record<EvaluationTypeValue, string>>;
    availabilityByType: TemplateAvailabilityByType;
}

// Every type is judged, optional template included: a stale id would reach the import and fail.
export const getTypesWithUnavailableTemplate = ({
    selectedTypes,
    templateIds,
    availabilityByType,
}: ClassifyStoredTemplatesParams): EvaluationTypeValue[] =>
    selectedTypes.filter((type) => {
        const availability = availabilityByType[type];
        const templateId = templateIds[type];

        return (
            !!templateId &&
            canLibraryTestifyToDeletion(availability) &&
            !isTemplateStillAvailable(templateId, availability.pages, availability.amelioTemplates)
        );
    });

export const getTypesWithUnresolvableTemplate = ({
    selectedTypes,
    templateIds,
    availabilityByType,
}: ClassifyStoredTemplatesParams): EvaluationTypeValue[] =>
    selectedTypes.filter(
        (type) => !!templateIds[type] && isLibrarySettledAndEmpty(availabilityByType[type]),
    );

const toTemplateOptions = (templates: ReviewTemplate[]): OptionProps[] =>
    templates.map((template) => ({
        label: getLocalizedText(template.title),
        value: template.id,
    }));

interface BuildTemplateOptionsByTypeParams {
    noAdditionalTemplateLabel: string;
    managerTemplatePages?: TemplatePages;
    feedback360TemplatePages?: TemplatePages;
    selfAssessmentTemplatePages?: TemplatePages;
    amelioTemplatesByType?: Partial<Record<EvaluationTypeValue, ReviewTemplate[]>>;
}

export const buildTemplateOptionsByType = ({
    noAdditionalTemplateLabel,
    managerTemplatePages,
    feedback360TemplatePages,
    selfAssessmentTemplatePages,
    amelioTemplatesByType,
}: BuildTemplateOptionsByTypeParams): Record<EvaluationTypeValue, OptionProps[]> => {
    const amelioTemplatesOf = (type: EvaluationTypeValue) => amelioTemplatesByType?.[type] ?? [];

    const managerOptions = [
        ...toTemplateOptions(flattenTemplatePages(managerTemplatePages)),
        ...toTemplateOptions(amelioTemplatesOf(EvaluationType.MANAGER_EVALUATION)),
    ];

    return {
        [EvaluationType.MANAGER_EVALUATION]:
            managerOptions.length > 0
                ? [
                      { label: noAdditionalTemplateLabel, value: NO_ADDITIONAL_TEMPLATE_VALUE },
                      ...managerOptions,
                  ]
                : [],
        [EvaluationType.EVALUATION_360]: toTemplateOptions([
            ...flattenTemplatePages(feedback360TemplatePages),
            ...amelioTemplatesOf(EvaluationType.EVALUATION_360),
        ]),
        [EvaluationType.SELF_EVALUATION]: toTemplateOptions([
            ...flattenTemplatePages(selfAssessmentTemplatePages),
            ...amelioTemplatesOf(EvaluationType.SELF_EVALUATION),
        ]),
    };
};
